import { Injectable } from '@angular/core';
import { SHA256 } from 'crypto-js';

type DataLayerWindow = Window & { dataLayer?: Record<string, unknown>[] };

export interface DataLayerUserDataInput {
  email?: string | null;
  phone?: string | null;
  externalId?: string | number | null;
}

export interface DataLayerUserData {
  user_email: string;
  user_phone: string;
  user_external_id: string;
}

@Injectable({
  providedIn: 'root'
})
export class DataLayerService {

  // Campos de usuario que el pixel de TikTok necesita tener en el dataLayer antes del evento
  private readonly userDataKeys = ['user_email', 'user_phone', 'user_external_id'];
  private readonly userDataDelayMs = 300;

  constructor() { }

  /**
   * Envía un evento al dataLayer de GTM; data se mezcla al mismo nivel que event.
   * Si data trae datos de usuario, primero los envía con 'user_data_ready' (sin activador en GTM)
   * y espera 300 ms, para que el pixel de TikTok ya los tenga cuando salga el evento.
   * Es async: haz await antes de redirigir para no perder el evento.
   */
  async pushEvent(event: string, data: Record<string, unknown> = {}): Promise<void> {
    try {
      const w = window as DataLayerWindow;
      const dataLayer = w.dataLayer = w.dataLayer || [];

      const userData = this.userDataKeys
        .filter(key => !!data[key])
        .reduce((acc, key) => ({ ...acc, [key]: data[key] }), {} as Record<string, unknown>);

      if (Object.keys(userData).length) {
        dataLayer.push({ ...userData, event: 'user_data_ready' });
        await new Promise(resolve => setTimeout(resolve, this.userDataDelayMs));
      }

      // event al final para que ninguna propiedad de data lo sobrescriba
      dataLayer.push({ ...data, event });
    } catch (error) {
      console.error('Error al enviar evento al dataLayer:', error);
    }
  }

  // Normaliza y hashea (SHA256) los datos de usuario para match en plataformas de ads; '' cuando el dato no está disponible
  buildUserData(input: DataLayerUserDataInput): DataLayerUserData {
    const email = String(input.email || '').trim().toLowerCase();
    const phone = this.toE164MxPhone(String(input.phone || ''));
    const externalId = String(input.externalId || '').trim();

    return {
      user_email: this.hash(email),
      user_phone: this.hash(phone),
      user_external_id: this.hash(externalId),
    };
  }

  // El formulario solo acepta teléfonos de México a 10 dígitos
  private toE164MxPhone(phone: string): string {
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) { return '+52' + digits; }
    if (digits.length === 12 && digits.startsWith('52')) { return '+' + digits; }
    return '';
  }

  private hash(value: string): string {
    return value ? SHA256(value).toString() : '';
  }
}
